import {BookOfShadowsTextStyles} from "../../config/BookOfShadowsTextStyles";
import {BasicBitmapCountUp} from "../../display/BasicBitmapCountUp";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {BookOfShadowsGameConfig} from "../../BookOfShadowsGameConfig";
import {BookOfShadowsTransitionsView} from "../BookOfShadowsTransitionsView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {TweenLite} from "gsap";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {IResizeData} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {BookOfShadowsParsedGameData} from "../../server/data/BookOfShadowsParsedGameData";
import {BookOfShadowsGameModuleConfig} from "../../BookOfShadowsGameModuleConfig";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {BookOfShadows} from "../../BookOfShadows";
import {SoundManager} from "../../sound/SoundManager";
import {BookOfShadowsSoundConfig} from "../../BookOfShadowsSoundConfig";

export class BookOfShadowsFreeSpinOutroView extends BookOfShadowsTransitionsView {
    private _gameGraphics: PIXI.Container;
    private _youWonText: PIXI.Text;
    private _totalWin: number = 0;
    private _bitmapText: BasicBitmapCountUp;
    private _bookAnimation: PIXI.spine.Spine;
    private _fire: TimelineSprite;
    private _centerPos: number[] = [BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2, (BookOfShadowsGameConfig.instance.REEL_AREA_HEIGHT / 2) + BookOfShadowsGameConfig.instance.REEL_AREA_POS_Y];

    private _winMoreThanFeatureBuyCost: boolean = false;
    private _textLoopTween: SpineTween;

    // #331
    private _totalWinBelowStake: boolean = false;

    /**
     * @constructs
     * @param layer
     * @param callBack
     */
    constructor(layer: ScalableLayer, callBack: () => void) {
        super(layer, callBack);

        this._layer.addChild(this);
    }

    /**
     * @description To prepare the data for this view.
     * @param data
     */
    public prepare(data: BookOfShadowsParsedGameData): void {
        const activeRows: number = data.selectedRowsFS,
            totalWinToPlayedBetRatio: number = (data.totalWin / data.playedBetValue);

        this._totalWin = data.totalWin;

        this._winMoreThanFeatureBuyCost = false;

        // #76
        if (activeRows === 5) {
            this._winMoreThanFeatureBuyCost = totalWinToPlayedBetRatio > 120;
        } else if (activeRows === 4) {
            this._winMoreThanFeatureBuyCost = totalWinToPlayedBetRatio > 100;
        } else if (activeRows === 3) {
            this._winMoreThanFeatureBuyCost = totalWinToPlayedBetRatio > 80;
        }

        // #331
        this._totalWinBelowStake = BookOfShadowsGameModuleConfig.featurePrice.isWinBelowStake();
    }

    /**
     * @description To start animation for this view.
     */
    public startAnimation(): void {
        const animName: string = (this._winMoreThanFeatureBuyCost ? "FS_OutroFire" : "FS_Outro"),
            tween: SpineTween = new SpineTween(this._bookAnimation, 0, animName, 0);

        this._fire.show();
        this._fire.playLoop();

        this._bookAnimation.renderable = true;
        this._bookAnimation.alpha = 0;
        TweenLite.to(this._bookAnimation, 0.001, {alpha: 1});

        TweenLite.delayedCall(tween.totalDuration(), () => {
            // #331
            const duration: number = (this._totalWinBelowStake ? 0 : 2);

            this._youWonText.visible = !this._totalWinBelowStake;

            this._gameGraphics.visible = true;
            this._gameGraphics.alpha = 0;
            TweenLite.to(this._gameGraphics, 0.3, {alpha: 1});

            this._bitmapText.visible = true;
            this._bitmapText.alpha = 0;
            this._bitmapText.scale.set(2.5);
            this._bitmapText.preCalculateScale(0, this._totalWin);
            this._bitmapText.getCountAnimation(0, this._totalWin, duration);
            if (!this._totalWinBelowStake) {
                SoundManager.playWinCountUp();
            }
            TweenLite.to(this._bitmapText, 0.1, {alpha: 1});
            TweenLite.to(this._bitmapText.scale, 0.1, {x: 0.5, y: 0.5});
            TweenLite.to(this._bitmapText.scale, 0.2, {x: 1, y: 1, delay: 0.1});

            if (this._winMoreThanFeatureBuyCost) {
                BookOfShadowsGameModuleConfig.bigWinFountain.startFountainForOutro();

                this._textLoopTween = new SpineTween(this._bookAnimation, 0, "FS_OutroFireLoop", -1);
            }

            TweenLite.delayedCall(duration, () => {
                if (!this._totalWinBelowStake) {
                    SoundManager.stopSoundEffect(BookOfShadowsSoundConfig.instance.WIN_COUNT_UP);
                    SoundManager.playWinCountUpEnd();
                }

                this._layer.addChild(this._button);
                this._button.visible = true;

                // handling replay
                if (GameSetting.replayMode) {
                    TweenLite.delayedCall(1, () => this._completeCallback());
                } else {
                    this.enableButton();
                }
            });
        });
    }

    /**
     * @inheritDoc
     */
    public cleanUp(): void {
        this._fire.hide();
        this._bookAnimation.renderable = false;
        this._bitmapText.visible = false;
        this._gameGraphics.visible = false;

        if (this._textLoopTween) {
            this._textLoopTween.clear();
            this._textLoopTween.kill();
        }

        BookOfShadowsGameModuleConfig.bigWinFountain.stopFountain();
        BookOfShadowsGameModuleConfig.featurePrice.reset();

        this._button.visible = false;
        this.disableButton();
    }

    /**
     * @inheritDoc
     * @param resizeData
     */
    protected onResize(resizeData: IResizeData): void {
        super.onResize && super.onResize(resizeData);
        this._fire.position.set(BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2, NolimitApplication.screenBounds.bottom - 130);
    }

    /**
     * @inheritDoc
     */
    protected initAnimations(): void {
        const xOffsetBitmapText: number = 185,
            yOffsetBitmapText = -80,
            maxWidthOffset: number = 20;

        super.initAnimations();

        this.createFire();

        this._bookAnimation = new PIXI.spine.Spine(GameResources.getSpineAsset("S_spine"));
        this._bookAnimation.renderable = false;
        this._bookAnimation.position.set(this._centerPos[0], this._centerPos[1]);

        this._bitmapText = new BasicBitmapCountUp(BookOfShadowsTextStyles.FS_OUTRO_COUNT_UP, (BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2) - maxWidthOffset, new PIXI.Point(0.5, 0.5));
        this._bitmapText.visible = false;
        this._bitmapText.position.set(this._centerPos[0] + xOffsetBitmapText, this._centerPos[1] + yOffsetBitmapText);

        this.addChild(this._fire, this._bookAnimation, this.createTextItems(), this._bitmapText);

        // making sure that the button is sorted on top in the layer.
        this._button.visible = false;
        this._layer.addChild(this._button);
    }

    /**
     * @description To create the fire animation for this view.
     */
    private createFire(): void {
        this._fire = new TimelineSprite(GameResources.getTextures("bigWinFire"));
        this._fire.hide();
        this._fire.anchor.set(0.5);
        this._fire.blendMode = PIXI.BLEND_MODES.ADD;
        this._fire.scale.set(2, 5);
    }

    /**
     * @description To create the text items for this view.
     */
    private createTextItems(): PIXI.DisplayObject {
        const xOffset: number = 178,
            yOffsetYouWon = 170;

        this._gameGraphics = new PIXI.Container();
        this._gameGraphics.name = "_gameGraphics";

        this._youWonText = new PIXI.Text(BookOfShadows.api.translations.translate("TOTAL WIN"), BookOfShadowsTextStyles.DEFAULT_SCREEN_TEXT);
        this._youWonText.anchor.set(0.5);
        this._youWonText.position.set(this._centerPos[0] + xOffset, this._centerPos[1] - yOffsetYouWon);

        this._gameGraphics.visible = false;
        this._gameGraphics.addChild(this._youWonText);

        return this._gameGraphics;
    }
}
